import {
  App,
  Button,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  RollbackOutlined,
  StopOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  inventoryApi,
  type Asset,
  type AssetCondition,
  type AssetInput,
  type AssetStatus,
} from '@/api/inventory';
import { financeApi } from '@/api/finance';
import { setupApi } from '@/api/setup';
import { hrApi } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { PhotoUpload } from '@/shared/components/photo-upload';
import { extractError } from '@/shared/lib/error-message';
import { usePhotoUpload } from '@/shared/lib/use-photo-upload';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const STATUS_COLOR: Record<AssetStatus, string> = {
  IN_USE: 'processing',
  IN_STORAGE: 'default',
  DISPOSED: 'red',
};

interface FormShape {
  assetCode: string;
  name: string;
  assetTypeId: number;
  description?: string;
  purchaseDate?: Dayjs;
  purchaseCost?: number;
  supplierName?: string;
  location?: string;
  photoPath?: string | null;
  condition: AssetCondition;
}

export function AssetsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('INVENTORY:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('INVENTORY:EDIT'));

  const [statusFilter, setStatusFilter] = useState<AssetStatus | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Asset | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<Asset | null>(null);
  const [form] = Form.useForm<FormShape>();
  const photo = usePhotoUpload('asset-photos');

  const { data, isFetching } = useQuery({
    queryKey: ['inventory', 'assets', { statusFilter, typeFilter, search }],
    queryFn: () => inventoryApi.listAssets({ status: statusFilter, assetTypeId: typeFilter, q: search.trim() || undefined }),
  });

  const { data: types } = useQuery({
    queryKey: ['setup', 'lookups', 'asset-types', 'active'],
    queryFn: () => setupApi.listLookup('asset-types', true),
    staleTime: 5 * 60_000,
  });

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body: AssetInput = {
        assetCode: values.assetCode.trim().toUpperCase(),
        name: values.name.trim(),
        assetTypeId: values.assetTypeId,
        description: values.description?.trim() || undefined,
        purchaseDate: values.purchaseDate ? values.purchaseDate.format('YYYY-MM-DD') : null,
        purchaseCost: values.purchaseCost != null ? Number(values.purchaseCost).toFixed(2) : undefined,
        supplierName: values.supplierName?.trim() || undefined,
        location: values.location?.trim() || undefined,
        photoPath: values.photoPath ?? null,
        condition: values.condition,
      };
      return editing ? inventoryApi.updateAsset(editing.id, body) : inventoryApi.createAsset(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Asset updated' : 'Asset created');
      qc.invalidateQueries({ queryKey: ['inventory', 'assets'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ condition: 'NEW' });
  };
  const openEdit = (row: Asset) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      assetCode: row.assetCode,
      name: row.name,
      assetTypeId: row.assetTypeId,
      description: row.description ?? undefined,
      purchaseDate: row.purchaseDate ? dayjs(row.purchaseDate) : undefined,
      purchaseCost: row.purchaseCost ? Number(row.purchaseCost) : undefined,
      supplierName: row.supplierName ?? undefined,
      location: row.location ?? undefined,
      photoPath: row.photoPath ?? null,
      condition: row.condition,
    });
  };
  const open = creating || editing != null;

  const columns: TableProps<Asset>['columns'] = [
    {
      title: 'Code',
      dataIndex: 'assetCode',
      width: 110,
      render: (c: string) => <Tag color="processing" style={{ fontFamily: 'monospace' }}>{c}</Tag>,
    },
    {
      title: 'Asset',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Space size={6}>
            {row.assetTypeName && <Tag>{row.assetTypeName}</Tag>}
            <Tag>{row.condition}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: AssetStatus) => <Tag color={STATUS_COLOR[s]}>{s.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Assigned to',
      width: 200,
      render: (_, row) =>
        row.currentAssignment ? (
          <Space orientation="vertical" size={0}>
            {row.currentAssignment.assignedToStaffName && (
              <Typography.Text>{row.currentAssignment.assignedToStaffName}</Typography.Text>
            )}
            {row.currentAssignment.assignedToLocation && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.currentAssignment.assignedToLocation}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">{row.location ?? '—'}</Typography.Text>
        ),
    },
    {
      title: 'Cost',
      dataIndex: 'purchaseCost',
      width: 120,
      align: 'right',
      render: (v: string | null) => v ? `PKR ${PKR(v)}` : '—',
    },
    {
      title: '',
      width: 160,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setViewing(row)} />
          {canEdit && row.status !== 'DISPOSED' && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Inventory"
        title="Fixed assets"
        description="Furniture, computers, lab equipment. Track location, condition, assignment, and disposal."
        count={{ value: data?.length ?? 0, label: 'assets' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New asset
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Select
          allowClear
          placeholder="Status"
          style={{ minWidth: 160 }}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
          options={(['IN_USE', 'IN_STORAGE', 'DISPOSED'] as AssetStatus[]).map((s) => ({
            value: s,
            label: s.replace('_', ' '),
          }))}
        />
        <Select
          allowClear
          placeholder="Type"
          style={{ minWidth: 160 }}
          value={typeFilter}
          onChange={(v) => setTypeFilter(v)}
          options={(types ?? []).map((t) => ({ value: t.id, label: t.name }))}
        />
        <Input.Search
          allowClear
          placeholder="Search code / name / location"
          style={{ width: 280 }}
          onSearch={(v) => setSearch(v)}
        />
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No assets in this view" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isFetching}
          columns={columns}
          dataSource={data ?? []}
          pagination={{ pageSize: 25 }}
        />
      )}

      <Drawer
        title={editing ? `Edit · ${editing.assetCode}` : 'New asset'}
        open={open}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        size={520}
        destroyOnHidden
        extra={
          <Space>
            <Button
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={upsert.isPending} onClick={() => form.submit()}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        {open && (
          <Form
            form={form}
            layout="vertical"
            onFinish={async (v) => {
              try {
                const uploaded = await photo.commit();
                if (uploaded !== undefined) v = { ...v, photoPath: uploaded };
              } catch (e) {
                message.error(extractError(e, 'Photo upload failed'));
                return;
              }
              upsert.mutate(v);
            }}
          >
            <Form.Item label="Photo" name="photoPath">
              <PhotoUpload
                category="asset-photos"
                shape="square"
                size={80}
                onPendingChange={photo.onPendingChange}
              />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Asset code" name="assetCode" rules={[{ required: true, max: 30 }]} style={{ width: 180 }}>
                <Input placeholder="e.g. LAB-PC-001" />
              </Form.Item>
              <Form.Item label="Name" name="name" rules={[{ required: true, max: 200 }]} style={{ flex: 1 }}>
                <Input />
              </Form.Item>
            </Space.Compact>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Type" name="assetTypeId" rules={[{ required: true }]} style={{ flex: 1 }}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={(types ?? []).map((t) => ({ value: t.id, label: t.name }))}
                />
              </Form.Item>
              <Form.Item label="Condition" name="condition" rules={[{ required: true }]} style={{ width: 160 }}>
                <Select
                  options={(['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'] as AssetCondition[]).map((c) => ({
                    value: c,
                    label: c,
                  }))}
                />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Description" name="description" rules={[{ max: 5000 }]}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Purchase date" name="purchaseDate" style={{ flex: 1 }}>
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
              <Form.Item label="Cost (PKR)" name="purchaseCost" style={{ flex: 1 }}>
                <InputNumber style={{ width: '100%' }} min={0} step={1000} />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Supplier" name="supplierName" rules={[{ max: 200 }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Location" name="location" rules={[{ max: 200 }]}>
              <Input placeholder="e.g. Computer lab — second floor" />
            </Form.Item>
          </Form>
        )}
      </Drawer>

      {viewing && <AssetDetailDrawer asset={viewing} onClose={() => setViewing(null)} canEdit={canEdit} />}
    </div>
  );
}

function AssetDetailDrawer({
  asset,
  onClose,
  canEdit,
}: {
  asset: Asset;
  onClose: () => void;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [assigning, setAssigning] = useState(false);
  const [disposing, setDisposing] = useState(false);
  const [assignForm] = Form.useForm<{ staffId?: number; location?: string; remarks?: string }>();
  const [disposeForm] = Form.useForm<{ disposalDate: Dayjs; reason: string; disposalValue?: number }>();

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const staff = staffPage?.rows ?? [];

  const { data: detail } = useQuery({
    queryKey: ['inventory', 'assets', asset.id],
    queryFn: () => inventoryApi.getAsset(asset.id),
    initialData: asset,
  });

  const assign = useMutation({
    mutationFn: (v: { staffId?: number; location?: string; remarks?: string }) =>
      inventoryApi.assignAsset(asset.id, {
        staffId: v.staffId,
        location: v.location?.trim() || undefined,
        remarks: v.remarks?.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('Asset assigned');
      qc.invalidateQueries({ queryKey: ['inventory'] });
      setAssigning(false);
      assignForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const returnAsset = useMutation({
    mutationFn: () => inventoryApi.returnAsset(asset.id),
    onSuccess: () => {
      message.success('Asset returned to storage');
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts', 'active'],
    queryFn: () => financeApi.listBankAccounts(),
    staleTime: 5 * 60 * 1000,
    enabled: disposing,
  });
  const banks = (banksQuery.data ?? []).filter((b) => b.active);

  const dispose = useMutation({
    mutationFn: (v: { disposalDate: Dayjs; reason: string; disposalValue?: number; bankAccountId?: number }) =>
      inventoryApi.disposeAsset(asset.id, {
        disposalDate: v.disposalDate.format('YYYY-MM-DD'),
        reason: v.reason.trim(),
        disposalValue: v.disposalValue != null ? Number(v.disposalValue).toFixed(2) : undefined,
        bankAccountId: v.disposalValue != null && Number(v.disposalValue) > 0 ? v.bankAccountId ?? null : null,
      }),
    onSuccess: () => {
      message.success('Asset disposed');
      qc.invalidateQueries({ queryKey: ['inventory'] });
      setDisposing(false);
      disposeForm.resetFields();
      onClose();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedDisposalValue = Form.useWatch('disposalValue', disposeForm);

  const a = detail!;

  return (
    <Drawer open onClose={onClose} title={`${a.assetCode} · ${a.name}`} size={560}>
      <Space orientation="vertical" style={{ width: '100%' }} size={12}>
        <Space wrap>
          {a.assetTypeName && <Tag>{a.assetTypeName}</Tag>}
          <Tag color={STATUS_COLOR[a.status]}>{a.status.replace('_', ' ')}</Tag>
          <Tag>{a.condition}</Tag>
        </Space>
        {a.description && <Typography.Paragraph type="secondary">{a.description}</Typography.Paragraph>}
        <Space orientation="vertical" size={2}>
          {a.purchaseDate && <Typography.Text>Purchased {dayjs(a.purchaseDate).format('D MMM YYYY')}</Typography.Text>}
          {a.purchaseCost && <Typography.Text>Cost · PKR {PKR(a.purchaseCost)}</Typography.Text>}
          {a.supplierName && <Typography.Text type="secondary">Supplier: {a.supplierName}</Typography.Text>}
          {a.location && <Typography.Text type="secondary">Location: {a.location}</Typography.Text>}
        </Space>

        {a.status === 'DISPOSED' ? (
          <Tag color="red">
            Disposed {dayjs(a.disposalDate ?? '').format('D MMM YYYY')} · {a.disposalReason}
            {a.disposalValue ? ` · salvage PKR ${PKR(a.disposalValue)}` : ''}
          </Tag>
        ) : (
          canEdit && (
            <Space wrap>
              <Button icon={<UserSwitchOutlined />} onClick={() => setAssigning(true)}>
                Assign
              </Button>
              {a.status === 'IN_USE' && (
                <Button icon={<RollbackOutlined />} onClick={() => returnAsset.mutate()} loading={returnAsset.isPending}>
                  Return
                </Button>
              )}
              <Button danger icon={<StopOutlined />} onClick={() => {
                disposeForm.resetFields();
                disposeForm.setFieldsValue({ disposalDate: dayjs() });
                setDisposing(true);
              }}>
                Dispose
              </Button>
            </Space>
          )
        )}

        <Typography.Text strong>Movement history</Typography.Text>
        <Table
          rowKey="id"
          size="small"
          dataSource={a.movements}
          pagination={false}
          columns={[
            {
              title: 'Assigned',
              dataIndex: 'assignedAt',
              width: 130,
              render: (t: string) => dayjs(t).format('D MMM YYYY'),
            },
            {
              title: 'To',
              render: (_, row) =>
                row.assignedToStaffName ?? row.assignedToLocation ?? <span style={{ opacity: 0.4 }}>—</span>,
            },
            {
              title: 'Returned',
              dataIndex: 'returnedAt',
              width: 130,
              render: (t: string | null) =>
                t ? dayjs(t).format('D MMM YYYY') : <Tag color="processing">Open</Tag>,
            },
          ]}
        />
      </Space>

      <Modal
        title={`Assign ${a.assetCode}`}
        open={assigning}
        onCancel={() => setAssigning(false)}
        onOk={() => assignForm.submit()}
        confirmLoading={assign.isPending}
        okText="Assign"
        destroyOnHidden
      >
        {assigning && (
          <Form form={assignForm} layout="vertical" onFinish={(v) => assign.mutate(v)}>
            <Form.Item label="To staff (optional)" name="staffId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
              />
            </Form.Item>
            <Form.Item label="Or location" name="location" rules={[{ max: 200 }]}>
              <Input placeholder="e.g. Computer lab — 2nd floor" />
            </Form.Item>
            <Form.Item label="Remarks" name="remarks" rules={[{ max: 1000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={`Dispose ${a.assetCode}`}
        open={disposing}
        onCancel={() => setDisposing(false)}
        onOk={() => disposeForm.submit()}
        confirmLoading={dispose.isPending}
        okText="Dispose"
        okButtonProps={{ danger: true }}
        destroyOnHidden
      >
        {disposing && (
          <Form form={disposeForm} layout="vertical" onFinish={(v) => dispose.mutate(v)}>
            <Form.Item label="Disposal date" name="disposalDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item label="Reason" name="reason" rules={[{ required: true, max: 1000 }]}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>
            <Form.Item label="Salvage value (optional)" name="disposalValue">
              <InputNumber style={{ width: '100%' }} min={0} step={500} addonBefore="PKR" />
            </Form.Item>
            {Number(watchedDisposalValue ?? 0) > 0 && (
              <Form.Item
                label="Deposit proceeds to"
                name="bankAccountId"
                rules={[{ required: true, message: 'Required when there are sale proceeds' }]}
                tooltip="Posts DR Cash/Bank · CR Miscellaneous Income."
              >
                <Select
                  loading={banksQuery.isLoading}
                  placeholder="Cash drawer or bank"
                  options={banks.map((b) => ({
                    value: b.id,
                    label: `${b.accountName}${b.bankName ? ` · ${b.bankName}` : ''}${b.cash ? ' (cash)' : ''}`,
                  }))}
                />
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>
    </Drawer>
  );
}
